import { useMemo } from 'react'

import { areaRangeOf } from 'utils/purchaseFlow/segmentForSurface'
import { useGetPropertyDetail } from 'dataQueries'
import type { Nullable } from 'models/helpers'
import type { RealEstatePropertyDTO } from 'models/property/realEstatePropertyDTO'

/**
 * PF-revamp-mandatory property fields. The completeness gate fires when any
 * of these are missing on the property the user wants to use for an order.
 *
 * These mirror the BE `resolveOrderableProperty` 422 checks the PF v2
 * create-session endpoint runs (Oscar, Slice C fix-wave):
 *   1. Complete address — `street`, `streetNumber`, `postalCode`, `city` all
 *      non-blank (`RealEstatePropertyDTO.address.*`).
 *   2. Routable coordinates — `address.coordinates` present AND not `(0,0)`
 *      (modeled here as the `coordinates` field).
 *   3. Usable area — `surface.value` present and strictly positive
 *      (`RealEstatePropertyDTO.surface`, the platform `AreaDTO`).
 *
 * Residential / Commercial is NOT required: the flow does not ask for it at the
 * property level; the products that depend on it ask in their own configuration.
 *
 * `surface` is checked here as of 2026-08-18. It previously could not be: the
 * column existed on the entity but was absent from the FE-facing DTOs, so a
 * property with no area passed this gate and only failed at session-create — as an
 * opaque error that reads like an address problem, because the message does not
 * name the field. The backend now populates it on both the detail and list DTOs
 * (`RealEstatePropertyFactory.toSurfaceDTO`, null when `area` is null) and accepts
 * it on the create and patch payloads, so the requirement is caught up front and
 * is fixable inline like every other field here.
 */
export type RequiredPropertyField =
  | 'street'
  | 'streetNumber'
  | 'postalCode'
  | 'city'
  | 'coordinates'
  | 'surface'

export const REQUIRED_PROPERTY_FIELDS: ReadonlyArray<RequiredPropertyField> = [
  'street',
  'streetNumber',
  'postalCode',
  'city',
  'coordinates',
  'surface',
]

/** The address-level required fields. Fixed inline by re-entering the address
 *  (Google Places / manual) in the completion form — re-selecting yields routable
 *  coordinates, satisfying the `(0,0)` check. Deliberately excludes `surface`: it
 *  is not part of an address, and re-picking an address cannot supply it, so it
 *  has its own input in the completion form. */
export const ADDRESS_REQUIRED_FIELDS: ReadonlyArray<RequiredPropertyField> = [
  'street',
  'streetNumber',
  'postalCode',
  'city',
  'coordinates',
]

/**
 * Explicit status discriminant so callers can distinguish loading,
 * error, and ready without combining two booleans. `missing: []` is no longer
 * ambiguous: it means either "query in flight / errored" (status ≠ 'ready') or
 * "all fields present" (status = 'ready', isComplete = true).
 */
export type UsePropertyCompletenessStatus = 'loading' | 'error' | 'ready'

export interface UsePropertyCompletenessResult {
  /** Discriminates the three observable states of the underlying query. */
  status: UsePropertyCompletenessStatus
  /** True when all REQUIRED_PROPERTY_FIELDS are present on the property. */
  isComplete: boolean
  /** Fields the user must fill before the property can be locked in for ordering. */
  missing: ReadonlyArray<RequiredPropertyField>
  /** @deprecated Use `status === 'loading'`. Kept for backward compatibility. */
  isLoading: boolean
  /** @deprecated Use `status === 'error'`. Kept for backward compatibility. */
  isError: boolean
  /**
   * The property's ROUTABLE coordinates, or `undefined` when it has none (or the
   * null-island `(0,0)`, which is the same thing said badly).
   *
   * Surfaced because the caller has to put them in the store: the purchase-flow
   * store carried no coordinates for an existing-property selection, so the
   * instruction-options request fell back to `(0,0)` and the backend derived the
   * shoot timezone for a point in the Gulf of Guinea — returning null, which
   * silently disabled the lead-time floor and the timezone banner. This hook
   * already fetches the detail to judge completeness, so the value is free here
   * and would cost a second request anywhere else.
   */
  coordinates?: { lat: number; lng: number }
  /**
   * The closest routable point, when the property carries one. Travel distance
   * and creative dispatch belong on this; map pins and the timezone belong on
   * `coordinates`. Either being routable makes the property orderable.
   */
  routingCoordinates?: { lat: number; lng: number }
}

/** The address text fields the BE requires, in reporting order. */
const ADDRESS_TEXT_FIELDS = ['street', 'streetNumber', 'postalCode', 'city'] as const

/**
 * Which of the four required address text fields are missing.
 *
 * Blank-aware on purpose: the server tests `isNotBlank`
 * (`PropertyAddressExtension.hasCompleteTextFields`), so a whitespace-only
 * value is missing there and must be missing here too — a looser FE rule is
 * exactly how an unorderable property gets past the gate.
 *
 * Shared with the address ENTRY forms, not just this gate: a Google Places
 * result carries only the components Google holds, and
 * `getPropertyAddressFromGooglePlace` fills the absent ones with `''`. A pick
 * with no `street_number` therefore looked like a complete address, and the
 * property it created failed at session-create with INCOMPLETE_ADDRESS.
 */
export const missingAddressTextFields = (
  address?: Partial<Record<(typeof ADDRESS_TEXT_FIELDS)[number], string | null>> | null
): RequiredPropertyField[] =>
  ADDRESS_TEXT_FIELDS.filter((field) => !address?.[field]?.trim())

/**
 * The completeness rule itself, over an already-loaded property. Exported so
 * entry points that pre-select a property (and therefore already hold its
 * detail) can gate without issuing a second request.
 */
/** Present, and not the null-island `(0,0)` — which the BE rejects and which
 *  no geocoder ever legitimately returns for a European address. */
export const isRoutable = (coords?: { lat: number; lng: number } | null): boolean =>
  !!coords && coords.lat !== 0 && coords.lng !== 0

export const missingPropertyFields = (
  property: RealEstatePropertyDTO
): RequiredPropertyField[] => {
  const missing: RequiredPropertyField[] = []
  // BE `resolveOrderableProperty` checks 1 & 2 (Slice C fix-wave) — all
  // checkable from `RealEstatePropertyDTO.address`.
  const address = property.address
  missing.push(...missingAddressTextFields(address))

  // Routable coordinates: present AND not the null-island `(0,0)`. Manually
  // entered addresses that never geocoded come back as (0,0) and 422 server-side.
  //
  // EITHER field satisfies this. `creativeRoutingAddress` exists precisely for
  // the address that has no routable point of its own — a new build, a street
  // the map does not carry yet — so a property routable only through that pin is
  // orderable, and blocking it would defeat the field's purpose. Placement
  // agrees: it reads `creativeRoutingAddress ?: address`.
  if (!isRoutable(address?.coordinates) && !isRoutable(property.creativeRoutingAddress?.coordinates)) {
    missing.push('coordinates')
  }

  // BE check 3 — session-create rejects with MISSING_AREA when the property has no
  // area band, because product pricing is area-banded. Mirrors the server's own
  // rule: the band of a positive exact surface, else the range the client picked.
  if (areaRangeOf(property) === null) missing.push('surface')

  return missing
}

/**
 * Reports whether a property has all FE-mandatory fields filled in.
 *
 * Errors are reported via `isError` but isComplete stays false so the caller
 * doesn't accidentally let an unverified property through. Loading state
 * also returns isComplete=false until the detail query resolves.
 *
 * TODO(BE-typing): RealEstatePropertyDTO.propertyType is currently typed
 * against the FE's narrow PropertyType enum (RES/COM). BE actually returns a
 * 6-value PropertyType enum on this field per the 2026-04-30 BE chat — and
 * what we semantically want here is `propertyUsage`. The check still works
 * for the RES/COM cases that PF revamp cares about because both enums share
 * those values. Flip to `propertyUsage` when BE exposes it on detail.
 */
export function usePropertyCompleteness(
  propertyId: Nullable<string>,
  workspaceId: Nullable<string>
): UsePropertyCompletenessResult {
  // `asClient` is hardcoded (not a param): this hook exists only for the PF v2
  // entry flow, where the property belongs to the CLIENT being ordered for. The
  // workspace-scoped property endpoints serve the client representation, so an
  // admin's own mimetype 406s. See `PropertyRepresentationOptions`.
  const detail = useGetPropertyDetail(propertyId, workspaceId, true, { asClient: true })

  return useMemo<UsePropertyCompletenessResult>(() => {
    if (!propertyId || !workspaceId || detail.isLoading) {
      return { status: 'loading', isComplete: false, missing: [], isLoading: detail.isLoading, isError: false }
    }

    if (detail.isError || !detail.data) {
      return { status: 'error', isComplete: false, missing: [], isLoading: false, isError: true }
    }

    const missing = missingPropertyFields(detail.data)
    // Only hand back coordinates the BE can route to: `missing` already encodes
    // the null-island rule, so reuse it rather than re-testing lat/lng here.
    const addressCoords = detail.data.address?.coordinates
    const routingCoords = detail.data.creativeRoutingAddress?.coordinates

    return {
      status: 'ready',
      isComplete: missing.length === 0,
      missing,
      isLoading: false,
      isError: false,
      // Kept apart on purpose: the first says where the property IS, the second
      // where a creative can be sent. Callers pick by intent.
      coordinates: isRoutable(addressCoords) ? addressCoords : undefined,
      routingCoordinates: isRoutable(routingCoords) ? routingCoords : undefined,
    }
  }, [propertyId, workspaceId, detail.isLoading, detail.isError, detail.data])
}
